import React, { useCallback, useState } from 'react';

import pluralize from 'pluralize';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ledBrands } from '../../../../consts/brands';
import { LIST_VISIBILITY } from '../../../../consts/schedule';
import { isAgentHubSubBrand } from '../../../../utils/tenantUtils';
import ScheduleTable from '../common/ScheduleTable';
import { useScheduleSection } from '../common/useScheduleSection';
import { useUndoableDelete } from '../common/useUndoableDelete';
import { getLEDBrandName } from '../common/utils';

import LEDScheduleBulkUpdateContainer from './LEDScheduleBulkUpdateContainer';

interface Props {
  offerId: string;
  shouldMigrateSchedule: boolean;
  isExternallyDeleting?: boolean;
  refreshToken?: number;
}

function isLEDSchedule(schedule: App.Schedule) {
  return (
    schedule.type === LIST_VISIBILITY &&
    ledBrands.some((b) => schedule.brand === b.value && schedule.region === b.region.code) &&
    !isAgentHubSubBrand(schedule.brand as App.Brands)
  );
}

export default function LEDSchedules({
  offerId,
  shouldMigrateSchedule,
  isExternallyDeleting = false,
  refreshToken,
}: Props) {
  const {
    schedules,
    isLoading,
    error,
    updateErrorMessages,
    invalidateSchedules,
    onSaveFinished,
    clearErrors,
    onError,
    onUpdateErrors,
    onDelete,
    isRefetching,
    showSchedules,
  } = useScheduleSection({ offerId, shouldMigrateSchedule, refreshToken });

  const [isDeleteAllOpen, setIsDeleteAllOpen] = useState(false);

  const ledScheduleCount = schedules.filter(isLEDSchedule).length;

  const getIdList = useCallback(() => schedules.filter(isLEDSchedule).map((s) => s.id), [schedules]);

  const { confirm: executeDeleteAll, isDeleting: isDeletingAll } = useUndoableDelete({
    offerId,
    label: `All ${ledScheduleCount} Lifestyle Brands ${pluralize('schedule', ledScheduleCount)}`,
    getIdList,
    onSuccess: invalidateSchedules,
    onError,
  });

  const confirmDeleteAll = useCallback(() => {
    if (shouldMigrateSchedule) {
      return;
    }
    setIsDeleteAllOpen(false);
    executeDeleteAll();
  }, [shouldMigrateSchedule, executeDeleteAll]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h6">Lifestyle Brands</Typography>
          {window.configs?.DEFAULT_TIMEZONE && (
            <Typography variant="body2" color="text.secondary">
              All times in {window.configs.DEFAULT_TIMEZONE}
            </Typography>
          )}
          <Alert severity="info" variant="outlined" sx={{ mt: 1, py: 0 }}>
            <Typography variant="body2">When adding schedules, start from the top and work down.</Typography>
          </Alert>
        </Box>
        {showSchedules && (
          <Stack direction="row" gap={1}>
            <LEDScheduleBulkUpdateContainer
              offerId={offerId}
              schedules={schedules}
              type={LIST_VISIBILITY}
              onOpen={clearErrors}
              onSaveFinished={onSaveFinished}
              shouldMigrateSchedule={shouldMigrateSchedule}
              onError={invalidateSchedules}
            />
            {ledScheduleCount > 0 && (
              <PermissionedComponent>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setIsDeleteAllOpen(true)}
                  disabled={shouldMigrateSchedule}
                >
                  Delete All
                </Button>
              </PermissionedComponent>
            )}
          </Stack>
        )}
      </Stack>
      <Paper variant="outlined" sx={{ position: 'relative', p: { xs: 2, sm: 3, md: '24px 40px' }, overflow: 'hidden' }}>
        {isRefetching && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}
        {error && <Alert severity="error">{error.message}</Alert>}
        {updateErrorMessages && (
          <Alert severity="error">
            Error updating schedules:
            <ul>
              {updateErrorMessages.map((message, i) => (
                <li key={i}>{message}</li>
              ))}
            </ul>
          </Alert>
        )}
        {isLoading && (
          <Box py={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}
        {showSchedules && (
          <ScheduleTable
            offerId={offerId}
            schedules={schedules}
            types={[LIST_VISIBILITY]}
            onDelete={onDelete}
            onSaveFinished={onSaveFinished}
            onUpdateErrors={onUpdateErrors}
            shouldMigrateSchedule={shouldMigrateSchedule}
            isDeletingAll={isDeletingAll || isExternallyDeleting}
            columnLabel="Brand"
            getLabel={(s) => getLEDBrandName(s.brand)}
            filterFn={(s, type) =>
              s.type === type &&
              ledBrands.some((b) => s.brand === b.value && s.region === b.region.code) &&
              !isAgentHubSubBrand(s.brand as App.Brands)
            }
            sortFn={(a, b) => a.brand.localeCompare(b.brand)}
          />
        )}
      </Paper>
      <Dialog open={isDeleteAllOpen} onClose={() => setIsDeleteAllOpen(false)}>
        <DialogTitle>Delete all schedules?</DialogTitle>
        <DialogContent>
          <Typography>
            This will delete all {ledScheduleCount} Lifestyle Brands {pluralize('schedule', ledScheduleCount)}. You will
            have 5 seconds to undo.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            To avoid accidental removals, delete schedules from the bottom and work up.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteAllOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteAll} autoFocus>
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
